/**
 * BẰNG CHỨNG THU/CHI — ảnh chụp uỷ nhiệm chi / biên lai, hoặc PDF sao kê.
 *
 * Khác ImageGallery (ảnh thiết bị): KHÔNG resize, KHÔNG re-encode về JPEG. Bản scan
 * chứng từ phải giữ nguyên bản gốc để còn đối chiếu với ngân hàng, và PDF thì không
 * vẽ qua <canvas> được. Đổi lại backend chặn chặt ở khâu nhận (chỉ image/* +
 * application/pdf, PDF phải bắt đầu bằng "%PDF-") và luôn trả kèm nosniff —
 * xem cmd/mdmquery/ledgerfiles.go.
 *
 * kind: 'thu' | 'chi_hh' | 'chi_khac' · id: id dòng giao dịch trong bảng tương ứng.
 */
import { useEffect, useRef, useState } from 'react';
import * as query from '../api/query';
import { confirmVN } from './Dialog';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/heic,application/pdf';
const MAX_BYTES = 15 * 1024 * 1024; // khớp maxLedgerFileBytes bên server
const MAX_FILES = 10;               // khớp maxLedgerFiles

const sizeText = (n) => (n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB');

// DÁN ẢNH TỪ CLIPBOARD (Ctrl+V) — ảnh chụp màn hình biên lai là cách nhanh nhất, khỏi
// phải lưu ra file rồi mới chọn.
//
// Nghe sự kiện paste ở cấp document để người dùng khỏi phải bấm trúng ô trước, nhưng CÓ
// THỂ có nhiều ô đính kèm mở cùng lúc (modal thu tiền: ô trong form + ô của dòng vừa
// ghi). Nên các ô xếp hàng và chỉ ô MỞ SAU CÙNG nhận ảnh — đúng chỗ người dùng vừa thao
// tác tới — thay vì dán một lần mà ảnh nhảy vào cả hai nơi.
const pasteStack = [];

function usePasteFiles(onFiles) {
  const ref = useRef(onFiles);
  ref.current = onFiles;
  useEffect(() => {
    const me = {};
    pasteStack.push(me);
    const onPaste = (e) => {
      if (pasteStack[pasteStack.length - 1] !== me) return;
      const files = Array.from((e.clipboardData && e.clipboardData.files) || [])
        .filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
      if (!files.length) return; // dán chữ vào ô ghi chú thì kệ, không đụng tới
      e.preventDefault();
      ref.current(files);
    };
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('paste', onPaste);
      const i = pasteStack.indexOf(me);
      if (i >= 0) pasteStack.splice(i, 1);
    };
  }, []);
}

export default function LedgerEvidence({ kind, id, showToast, onCount }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [viewing, setViewing] = useState(null); // vị trí file đang xem, null = đóng

  const load = () => {
    if (!kind || !id) return;
    query.listLedgerFiles(kind, id)
      .then(list => { setItems(list || []); onCount && onCount((list || []).length); })
      .catch(e => showToast && showToast(e.message, 'error', '❌'))
      .finally(() => setLoaded(true));
  };
  useEffect(() => { load(); }, [kind, id]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // chọn lại đúng file đó lần nữa vẫn kích hoạt onChange
    add(files);
  };
  usePasteFiles(add);

  async function add(files) {
    if (!files.length) return;
    if (items.length + files.length > MAX_FILES) {
      showToast && showToast(`Mỗi giao dịch tối đa ${MAX_FILES} file`, 'error', '❌');
      return;
    }
    setBusy(true);
    try {
      for (const f of files) {
        if (f.size > MAX_BYTES) throw new Error(`"${f.name}" quá lớn (tối đa 15MB)`);
        await query.uploadLedgerFile(kind, id, f);
      }
      showToast && showToast(files.length > 1 ? `Đã đính kèm ${files.length} file` : 'Đã đính kèm', 'success', '📎');
      load();
    } catch (ex) {
      showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌');
      load();
    } finally { setBusy(false); }
  }

  async function del(f) {
    const what = f.orig_name || (f.is_pdf ? 'file PDF' : 'ảnh');
    if (!(await confirmVN(`Gỡ bằng chứng "${what}" khỏi giao dịch này?`,
      { title: 'Gỡ bằng chứng', okText: 'Gỡ', danger: true }))) return;
    try {
      await query.deleteLedgerFile(kind, id, f.id);
      showToast && showToast('Đã gỡ', 'success', '🗑');
      load();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  return (
    <div className="evi">
      <div className="evi-list">
        {items.map((f, i) => {
          const url = query.ledgerFileURL(kind, id, f.id);
          return (
            <div key={f.id} className="evi-item">
              {/* Bấm là xem NGAY trong trang (ảnh lẫn PDF) — xem EvidenceViewer. */}
              <button type="button" className="evi-open" onClick={() => setViewing(i)}
                title={`${f.orig_name || (f.is_pdf ? 'PDF' : 'Ảnh')} · ${sizeText(f.size)}${f.created_by ? ' · ' + f.created_by : ''}${f.created_at ? ' · ' + f.created_at : ''}`}>
                {f.is_pdf
                  ? <span className="evi-pdf">PDF</span>
                  : <img src={url} alt={f.orig_name || 'bằng chứng'} loading="lazy" />}
                <span className="evi-name">{f.orig_name || (f.is_pdf ? 'Tài liệu PDF' : 'Ảnh')}</span>
              </button>
              <button type="button" className="evi-del" title="Gỡ khỏi giao dịch" onClick={() => del(f)}>✕</button>
            </div>
          );
        })}
        {loaded && items.length === 0 && <span className="evi-empty">Chưa có bằng chứng nào</span>}
      </div>
      {viewing !== null && items.length > 0 && (
        <EvidenceViewer
          files={items} index={Math.min(viewing, items.length - 1)} onIndex={setViewing}
          onClose={() => setViewing(null)}
          urlOf={f => query.ledgerFileURL(kind, id, f.id)}
        />
      )}
      {items.length < MAX_FILES && (
        <div className="evi-actions">
          <label className={`act-btn evi-add${busy ? ' evi-busy' : ''}`}>
            {busy ? 'Đang tải lên…' : '📎 Thêm ảnh / PDF'}
            <input type="file" accept={ACCEPT} multiple disabled={busy} onChange={onPick} hidden />
          </label>
          <span className="evi-hint">hoặc dán ảnh bằng <b>Ctrl+V</b></span>
        </div>
      )}
    </div>
  );
}

/**
 * EvidenceViewer — xem chứng từ NGAY TRONG TRANG, không mở tab mới.
 *
 * Không dùng lại <Lightbox> của ImageUploader vì hai lý do: CSS của nó nằm trong khối
 * .dev-drawer (chỉ chạy trong ngăn thiết bị), và nó chỉ vẽ <img> nên không mở được
 * PDF. Ở đây PDF hiển thị bằng <iframe> — file cùng origin, trả về với
 * Content-Type: application/pdf + inline nên trình duyệt tự vẽ.
 *
 * Nhiều file thì lật bằng nút ‹ › , phím ← →, hoặc bấm thẳng vào dải thumbnail dưới
 * cùng; Esc để đóng.
 */
export function EvidenceViewer({ files, index, onIndex, onClose, urlOf }) {
  const n = files.length;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + n) % n);
      else if (e.key === 'ArrowRight') onIndex((index + 1) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, n, onIndex, onClose]);

  if (!n) return null;
  const cur = files[Math.min(index, n - 1)];
  const url = urlOf(cur);
  const label = cur.orig_name || (cur.is_pdf ? 'Tài liệu PDF' : 'Ảnh');

  return (
    <div className="evi-viewer" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="evi-vbar">
        <span className="evi-vname" title={label}>{label}</span>
        <span className="evi-vcount">{index + 1}/{n}</span>
        {/* download: tải thẳng về máy, KHÔNG mở tab mới. */}
        <a className="evi-vbtn" href={url} download={cur.orig_name || undefined} title="Tải về máy">⤓</a>
        <button type="button" className="evi-vbtn" title="Đóng (Esc)" onClick={onClose}>✕</button>
      </div>
      {n > 1 && <button type="button" className="evi-vnav evi-vprev" title="Trước (←)"
        onClick={() => onIndex((index - 1 + n) % n)}>‹</button>}
      <div className="evi-vstage" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        {cur.is_pdf
          ? <iframe className="evi-vpdf" src={url} title={label} />
          : <img className="evi-vimg" src={url} alt={label} />}
      </div>
      {n > 1 && <button type="button" className="evi-vnav evi-vnext" title="Sau (→)"
        onClick={() => onIndex((index + 1) % n)}>›</button>}
      {n > 1 && (
        <div className="evi-vstrip" onClick={e => e.stopPropagation()}>
          {files.map((f, i) => (
            <button type="button" key={f.id} className={`evi-vthumb${i === index ? ' on' : ''}`}
              title={f.orig_name || ''} onClick={() => onIndex(i)}>
              {f.is_pdf ? <span className="evi-pdf">PDF</span> : <img src={urlOf(f)} alt="" loading="lazy" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PendingEvidence — chọn chứng từ TRƯỚC khi giao dịch tồn tại.
 *
 * Lúc nhập phiếu thu/chi thì chứng từ đang cầm trên tay, nhưng chưa có id để gắn file
 * vào. Component này giữ file trong bộ nhớ trình duyệt; gọi uploadPending() ngay sau
 * khi server trả id là file bay lên.
 */
export function PendingEvidence({ files, setFiles, showToast }) {
  // Ảnh xem trước: tạo object URL MỘT lần cho mỗi file và thu hồi khi đổi/đóng form.
  // Tạo thẳng trong lúc render thì mỗi lần gõ phím lại sinh một URL mới, ảnh nháy liên tục.
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map(f => (f.type === 'application/pdf' ? '' : URL.createObjectURL(f)));
    setPreviews(urls);
    return () => urls.forEach(u => u && URL.revokeObjectURL(u));
  }, [files]);

  usePasteFiles(addFiles);

  function pick(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(picked);
  }

  function addFiles(picked) {
    if (!picked.length) return;
    const tooBig = picked.find(f => f.size > MAX_BYTES);
    if (tooBig) { showToast && showToast(`"${tooBig.name}" quá lớn (tối đa 15MB)`, 'error', '❌'); return; }
    if (files.length + picked.length > MAX_FILES) {
      showToast && showToast(`Mỗi giao dịch tối đa ${MAX_FILES} file`, 'error', '❌');
      return;
    }
    setFiles([...files, ...picked]);
  }
  return (
    <div className="evi">
      <div className="evi-list">
        {files.map((f, i) => (
          <div key={i} className="evi-item">
            <div className="evi-open" title={`${f.name} · ${sizeText(f.size)}`}>
              {f.type === 'application/pdf'
                ? <span className="evi-pdf">PDF</span>
                : previews[i] ? <img src={previews[i]} alt={f.name} /> : <span className="evi-pdf">FILE</span>}
              <span className="evi-name">{f.name}</span>
            </div>
            <button type="button" className="evi-del" title="Bỏ file này"
              onClick={() => setFiles(files.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
        {files.length === 0 && <span className="evi-empty">Ảnh uỷ nhiệm chi, biên lai, hoặc PDF sao kê</span>}
      </div>
      {files.length < MAX_FILES && (
        <div className="evi-actions">
          <label className="act-btn evi-add">
            📎 Chọn ảnh / PDF
            <input type="file" accept={ACCEPT} multiple onChange={pick} hidden />
          </label>
          <span className="evi-hint">hoặc dán ảnh bằng <b>Ctrl+V</b></span>
        </div>
      )}
    </div>
  );
}

/**
 * uploadPending đẩy các file đã chọn lên cho giao dịch VỪA tạo.
 *
 * Tiền đã ghi nhận xong rồi nên lỗi ở đây KHÔNG được làm hỏng thao tác: trả về số file
 * hỏng để nơi gọi báo "khoản thu đã ghi, còn N file chưa lên — đính kèm lại bằng nút 📎".
 */
export async function uploadPending(kind, id, files = []) {
  let failed = 0;
  for (const f of files) {
    try { await query.uploadLedgerFile(kind, id, f); } catch { failed++; }
  }
  return failed;
}

/**
 * LedgerTxnModal — CHI TIẾT một giao dịch trong sổ thu/chi.
 *
 * Dòng trong sổ chỉ có ngày/mã/mô tả gộp. Modal này tải phần đầy đủ từ server (kỳ,
 * hình thức, khách/CTV, hạng mục, ghi chú, ai ghi, ghi lúc nào) rồi đến phần bằng
 * chứng — xem và đính kèm thêm ngay tại đây.
 *
 * Nhãn của phần riêng theo loại giao dịch do SERVER trả về (fields), nên thêm loại
 * giao dịch mới không phải sửa chỗ này.
 */
export function LedgerTxnModal({ kind, id, onClose, onChanged, showToast }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [nFiles, setNFiles] = useState(null); // số file sau khi thêm/gỡ trong phiên này

  useEffect(() => {
    let alive = true;
    query.getLedgerTxn(kind, id)
      .then(x => { if (alive) setD(x); })
      .catch(e => { if (alive) setErr((e.body && e.body.error) || e.message); });
    return () => { alive = false; };
  }, [kind, id]);

  const money = (n) => (Number(n) || 0).toLocaleString('vi-VN') + ' ₫';
  const isThu = kind === 'thu';

  return (
    <div className="overlay open" onClick={e => { if (e.target.className === 'overlay open') { onClose(); } }}>
      <div className="modal" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-icon">{isThu ? '💰' : '📤'}</div>
            <div>
              <div className="modal-device-name">{d ? (d.code || 'Giao dịch') : 'Đang tải…'}</div>
              <div className="modal-device-sub">{d ? `${d.kind_label}${d.title ? ' · ' + d.title : ''}` : ''}</div>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="tab-body">
          {err && <div className="login-err">{err}</div>}
          {d && (
            <>
              <div className="txn-head">
                <div>
                  <span>Số tiền</span>
                  <b style={{ color: isThu ? 'var(--green)' : 'var(--red)' }}>{isThu ? '+' : '−'} {money(d.amount)}</b>
                </div>
                <div><span>Ngày giao dịch</span><b className="mono">{d.date || '—'}</b></div>
              </div>
              <div className="info-grid g2" style={{ marginTop: 12 }}>
                {d.fields.map((f, i) => (
                  <div className="info-cell" key={i}>
                    <div className="info-cell-label">{f.label}</div>
                    <div className="info-cell-val">{f.value || '—'}</div>
                  </div>
                ))}
                <div className="info-cell">
                  <div className="info-cell-label">Người ghi sổ</div>
                  <div className="info-cell-val">{d.created_by || '—'}</div>
                </div>
                <div className="info-cell">
                  <div className="info-cell-label">Ghi lúc</div>
                  <div className="info-cell-val mono">{d.created_at || '—'}</div>
                </div>
              </div>
              <div className="txn-sect">Bằng chứng ({nFiles ?? (d.files || []).length})</div>
              <LedgerEvidence kind={kind} id={id} showToast={showToast}
                onCount={n => { setNFiles(n); onChanged && onChanged(); }} />
              <div className="page-note" style={{ marginTop: 12 }}>
                Tối đa 10 file, mỗi file 15MB. File giữ NGUYÊN BẢN (không nén lại) để đối chiếu
                với ngân hàng. Gỡ khỏi giao dịch chỉ bỏ liên kết — bản gốc vẫn còn trên máy chủ.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** CSS dùng chung cho hai component trên — nhúng một lần ở App (GLOBAL_CSS). */
export const EVIDENCE_CSS = `
.evi{display:flex;flex-direction:column;gap:8px}
.evi-list{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.evi-item{position:relative}
.evi-open{display:flex;flex-direction:column;align-items:center;gap:4px;width:92px;padding:6px;
  border:1px solid var(--border);border-radius:10px;background:var(--bg3);text-decoration:none;color:var(--text2);
  font:inherit;text-align:center;cursor:pointer}
.evi-open:hover{border-color:var(--blue-border);background:var(--blue-bg)}
.evi-open img{width:78px;height:60px;object-fit:cover;border-radius:6px;display:block}
.evi-pdf{width:78px;height:60px;display:flex;align-items:center;justify-content:center;border-radius:6px;
  background:var(--red-bg);color:var(--red);font-weight:800;font-size:13px;letter-spacing:.04em}
.evi-name{font-size:11px;max-width:80px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.evi-del{position:absolute;top:-6px;right:-6px;width:20px;height:20px;border-radius:50%;border:1px solid var(--border);
  background:var(--bg2);color:var(--text3);font-size:11px;line-height:1;cursor:pointer;padding:0}
.evi-del:hover{background:var(--red-bg);color:var(--red);border-color:var(--red-border)}
.evi-empty{font-size:12px;color:var(--text3)}
.evi-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.evi-hint{font-size:12px;color:var(--text3)}
.evi-hint b{color:var(--text2)}
.evi-add{align-self:flex-start;cursor:pointer}
.evi-busy{opacity:.6;cursor:progress}
/* Đầu modal chi tiết giao dịch: số tiền + ngày */
.txn-head{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.txn-head>div{background:var(--bg3);border:1px solid var(--border);border-radius:10px;padding:10px 12px}
.txn-head span{display:block;font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.04em}
.txn-head b{display:block;font-size:18px;margin-top:3px;font-family:var(--head)}
@media(max-width:560px){.txn-head{grid-template-columns:1fr}}
.txn-sect{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin:16px 0 8px}
/* Trình xem chứng từ trong trang (ảnh + PDF). z-index trên cả modal chi tiết. */
.evi-viewer{position:fixed;inset:0;z-index:400;background:rgba(10,12,24,.88);display:flex;flex-direction:column;
  align-items:center;justify-content:center;gap:10px;padding:56px 16px 16px}
.evi-vbar{position:fixed;top:0;left:0;right:0;height:48px;display:flex;align-items:center;gap:10px;padding:0 12px;
  background:rgba(10,12,24,.55);color:#e8eaf2}
.evi-vname{flex:1;min-width:0;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.evi-vcount{font-size:12px;font-family:'JetBrains Mono',monospace;opacity:.8;flex:none}
.evi-vbtn{flex:none;width:34px;height:34px;border:none;border-radius:50%;background:rgba(255,255,255,.14);color:#fff;
  font-size:17px;line-height:34px;text-align:center;cursor:pointer;text-decoration:none;padding:0}
.evi-vbtn:hover{background:rgba(255,255,255,.3)}
.evi-vstage{flex:1;min-height:0;width:100%;display:flex;align-items:center;justify-content:center}
.evi-vimg{max-width:94vw;max-height:100%;object-fit:contain;border-radius:8px;background:#fff;box-shadow:0 16px 48px -10px rgba(0,0,0,.6)}
.evi-vpdf{width:min(980px,94vw);height:100%;border:0;border-radius:8px;background:#fff}
.evi-vnav{position:fixed;top:50%;transform:translateY(-50%);width:48px;height:48px;border:none;border-radius:50%;
  background:rgba(255,255,255,.14);color:#fff;font-size:32px;line-height:1;cursor:pointer}
.evi-vnav:hover{background:rgba(255,255,255,.3)}
.evi-vprev{left:14px}.evi-vnext{right:14px}
.evi-vstrip{flex:none;display:flex;gap:8px;overflow-x:auto;max-width:94vw;padding:6px}
.evi-vthumb{flex:none;width:64px;height:48px;padding:0;border:2px solid transparent;border-radius:6px;overflow:hidden;
  background:rgba(255,255,255,.1);cursor:pointer}
.evi-vthumb.on{border-color:#fff}
.evi-vthumb img{width:100%;height:100%;object-fit:cover;display:block}
.evi-vthumb .evi-pdf{width:100%;height:100%;font-size:11px;border-radius:0}
@media(max-width:560px){.evi-vnav{width:38px;height:38px;font-size:25px}.evi-vprev{left:6px}.evi-vnext{right:6px}}
`;
